import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { fetchUserProfile, saveUserProfile } from "../../shared/profileApi";
import { IconSave } from "../../shared/toolbarIcons";

function getInitial(value) {
  const trimmed = String(value || "A").trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : "A";
}

function formatRoleLabel(role) {
  if (!role) return "Administrator";
  return role.toLowerCase() === "admin"
    ? "Administrator"
    : role.charAt(0).toUpperCase() + role.slice(1);
}

function buildFallbackProfile(user) {
  const roleLabel = formatRoleLabel(user?.role);
  return {
    id: user?.id ?? 0,
    userName: user?.userName ?? "",
    role: user?.role ?? "",
    fullName: user?.displayName ?? user?.fullName ?? roleLabel,
    email: user?.email ?? ""
  };
}

export function MyProfilePage() {
  const { user, token, updateUser } = useAuth();
  const [profile, setProfile] = useState(() => buildFallbackProfile(user));
  const [fullName, setFullName] = useState(profile.fullName);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadProfile() {
      setLoading(true);
      setError("");

      if (!token || String(token).startsWith("local-")) {
        const fallback = buildFallbackProfile(user);
        if (!cancelled) {
          setProfile(fallback);
          setFullName(fallback.fullName);
          setLoading(false);
        }
        return;
      }

      const res = await fetchUserProfile(token);
      if (cancelled) return;

      if (!res.ok) {
        const fallback = buildFallbackProfile(user);
        setProfile(fallback);
        setFullName(fallback.fullName);
        setError("Could not load profile from API. Showing session details.");
        setLoading(false);
        return;
      }

      setProfile(res.data);
      setFullName(res.data.fullName);
      updateUser({
        id: res.data.id,
        userName: res.data.userName,
        role: res.data.role,
        displayName: res.data.fullName,
        fullName: res.data.fullName,
        email: res.data.email
      });
      setLoading(false);
    }

    void loadProfile();
    return () => {
      cancelled = true;
    };
  }, [token, user?.userName]);

  const roleLabel = useMemo(() => formatRoleLabel(profile.role), [profile.role]);

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const trimmed = fullName.trim();
    if (!trimmed) {
      setError("Full name is required.");
      return;
    }

    setSaving(true);

    if (!token || String(token).startsWith("local-")) {
      updateUser({ displayName: trimmed, fullName: trimmed });
      setProfile((prev) => ({ ...prev, fullName: trimmed }));
      setSuccess("Display name saved.");
      setSaving(false);
      return;
    }

    const res = await saveUserProfile(token, trimmed);
    setSaving(false);

    if (!res.ok) {
      let message = res.error ?? "Failed to save profile.";
      try {
        const parsed = JSON.parse(message);
        message = parsed.message ?? parsed.Message ?? message;
      } catch {
        // keep raw message
      }
      setError(message);
      return;
    }

    setProfile(res.data);
    setFullName(res.data.fullName);
    updateUser({
      displayName: res.data.fullName,
      fullName: res.data.fullName,
      email: res.data.email
    });
    setSuccess(res.message ?? "Profile updated successfully.");
  };

  if (loading) {
    return (
      <div className="profile-page">
        <p className="profile-page__loading">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <header className="profile-page__header">
        <h1>My Profile</h1>
        <p>Your admin account from the API login session.</p>
      </header>

      <div className="profile-page__top">
        <section className="profile-card profile-card--summary">
          <div className="profile-summary__avatar">{getInitial(profile.fullName)}</div>
          <div className="profile-summary__name">{profile.fullName}</div>
          <div className="profile-summary__role">{roleLabel}</div>
          <div className="profile-summary__handle">@{profile.userName}</div>
        </section>

        <section className="profile-card profile-card--form">
          <h2>Personal information</h2>
          <form onSubmit={handleSave} className="profile-form">
            {error && <div className="profile-alert profile-alert--error">{error}</div>}
            {success && <div className="profile-alert profile-alert--success">{success}</div>}

            <div className="profile-form__grid">
              <label className="profile-field">
                <span>Full name</span>
                <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
              </label>
              <label className="profile-field">
                <span>Email</span>
                <input type="email" value={profile.email || "—"} readOnly />
              </label>
              <label className="profile-field">
                <span>Username</span>
                <input type="text" value={profile.userName} readOnly />
              </label>
              <label className="profile-field">
                <span>Role</span>
                <input type="text" value={roleLabel} readOnly />
              </label>
            </div>

            <button type="submit" className="btn btn-primary profile-save-btn" disabled={saving}>
              <IconSave />
              {saving ? "Saving..." : "Save display name"}
            </button>
          </form>
        </section>
      </div>

      <section className="profile-card profile-card--account">
        <h2>Account</h2>
        <div className="profile-account-grid">
          <div className="profile-account-item">
            <div className="profile-account-item__icon profile-account-item__icon--user">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <div>
              <div className="profile-account-item__label">Signed in as</div>
              <div className="profile-account-item__value">{profile.userName}</div>
            </div>
          </div>
          <div className="profile-account-item">
            <div className="profile-account-item__icon profile-account-item__icon--shield">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <path d="m9 12 2 2 4-4" />
              </svg>
            </div>
            <div>
              <div className="profile-account-item__label">Access level</div>
              <div className="profile-account-item__value">{roleLabel}</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
